import { StyleSheet, css } from 'aphrodite';
import { subDays } from 'date-fns';
import { useMemo } from 'react';
import { useUserSession } from '../../../models/hooks/appStateHooks';
import { Alert, AlertActions, AlertDescription, AlertTitle } from '../../../web/components-v2/Alert';
import { Button } from '../../../web/components-v2/Button';
import { Skeleton } from '../../../web/components-v2/Skeleton';
import { SPACE, THEME } from '../../../web/components-v2/Theme/theme';
import {
	DataGrid,
	DataGridColumn,
	DataGridColumns,
	DataGridRow,
	DataGridRowItem,
	DataGridRows,
} from '../../../web/components/DataGrid';
import { LoadingSpinner } from '../../../web/components/LoadingSpinner';
import { ClockAlertIcon } from '../../../web/components/svgs/icons/ClockAlertIcon';
import { LockIcon } from '../../../web/components/svgs/icons/LockIcon';
import {
	ITimeEntryDay,
	ITimeEntryFilter,
	useMyOverdueTimeEntriesQuery,
	useTimeEntryDayQuery,
	useTimeEntryFilterQuery,
} from '../../queries/TimeEntry';
import { HoursEntryForm } from '../../timeTracking/components/HoursEntryForm';
import { LoadError } from '../../timeTracking/components/LoadError';
import { TimeEntryStatusPill } from '../../timeTracking/components/TimeEntryStatusPill';
import { formatDateTime, formatDayLong, formatDayShort, todayYmd } from '../../timeTracking/days';
import { formatHours } from '../../timeTracking/hours';
import { lockedDayMessage } from '../../timeTracking/lockedDay';
import { timeTrackingStyles as tt } from '../../timeTracking/styles';
import { BasicPage } from '../BasicPage';
import { ITimesheetRow, buildTimesheetRows, describeTimesheetRow } from './timesheetRows';
import { accessibilityStyles } from '../../../web/styles/styles';

const RECENT_DAYS = 30;

export function TimesheetOverview({
	canActAsSelf,
	onOpenDay,
}: {
	canActAsSelf: boolean;
	onOpenDay: (day: string) => void;
}) {
	const userSession = useUserSession();
	const today = todayYmd();
	const filter = useMemo<ITimeEntryFilter>(
		() => ({
			criteria: [
				{ property: 'User', value: userSession.user?.id },
				{ op: 'Gt', property: 'Day', value: todayYmd(subDays(new Date(), RECENT_DAYS)) },
			],
		}),
		[userSession.user?.id]
	);

	const todayQuery = useTimeEntryDayQuery({ day: today });
	const overdueQuery = useMyOverdueTimeEntriesQuery();
	const recentQuery = useTimeEntryFilterQuery({ enabled: !!userSession.user?.id, filter });

	const overdueDays = overdueQuery.data?.values ?? [];
	const rows = buildTimesheetRows({
		entries: recentQuery.data?.values ?? [],
		overdueDays,
		today: todayQuery.data,
	});

	return (
		<BasicPage appearance='light' title='Timesheet' bodyStyles={[tt.pageBody]}>
			{overdueDays.length ? <OverdueBanner days={overdueDays} onOpenDay={onOpenDay} /> : null}
			<div className={css(styles.columns)}>
				<section className={css(tt.card, styles.list)} aria-labelledby='timesheet-days-heading'>
					<div className={css(styles.listHeader)}>
						<h2 id='timesheet-days-heading' className={css(tt.cardTitle)}>
							Your days
						</h2>
						<span className={css(tt.muted)}>Last {RECENT_DAYS} days</span>
					</div>
					<DataGrid>
						<DataGridColumns>
							<DataGridColumn sticky>Day</DataGridColumn>
							<DataGridColumn sticky>Hours</DataGridColumn>
							<DataGridColumn sticky>Status</DataGridColumn>
							<DataGridColumn sticky>Details</DataGridColumn>
							<DataGridColumn sticky>
								<span className={css(accessibilityStyles.srOnly)}>Actions</span>
							</DataGridColumn>
						</DataGridColumns>
						<DataGridRows>
							{recentQuery.isLoading || overdueQuery.isLoading ? (
								<DataGridRow>
									<DataGridRowItem colSpan={5}>
										<LoadingSpinner type='large' />
									</DataGridRowItem>
								</DataGridRow>
							) : recentQuery.isError || overdueQuery.isError ? (
								<DataGridRow>
									<DataGridRowItem colSpan={5}>
										<LoadError
											message='We couldn’t load your days.'
											onRetry={() => {
												recentQuery.refetch();
												overdueQuery.refetch();
											}}
										/>
									</DataGridRowItem>
								</DataGridRow>
							) : rows.length ? (
								rows.map(row => (
									<TimesheetRow key={row.day} row={row} isToday={row.day === today} onOpenDay={onOpenDay} />
								))
							) : (
								<DataGridRow>
									<DataGridRowItem colSpan={5} styles={[tt.emptyCell]}>
										No hours in the last {RECENT_DAYS} days.
									</DataGridRowItem>
								</DataGridRow>
							)}
						</DataGridRows>
					</DataGrid>
				</section>
				<TodayCard
					day={today}
					data={todayQuery.data}
					isLoading={todayQuery.isLoading}
					isError={todayQuery.isError}
					onRetry={() => todayQuery.refetch()}
					canActAsSelf={canActAsSelf}
				/>
			</div>
		</BasicPage>
	);
}

function OverdueBanner({ days, onOpenDay }: { days: ITimeEntryDay[]; onOpenDay: (day: string) => void }) {
	const sentBack = days.filter(day => day.entry?.status === 'Rejected').length;
	return (
		<Alert tone='warning' icon={<ClockAlertIcon fillColor='currentColor' />}>
			<AlertTitle>{days.length === 1 ? '1 day needs your hours' : `${days.length} days need your hours`}</AlertTitle>
			<AlertDescription>
				{sentBack ? `${sentBack} sent back with a comment. ` : ''}
				Oldest: {formatDayLong(days[0].day)}
			</AlertDescription>
			<AlertActions>
				{days.slice(0, 3).map(day => (
					<Button key={day.day} type='button' variant='outline' size='sm' onClick={() => onOpenDay(day.day)}>
						{formatDayShort(day.day)}
					</Button>
				))}
			</AlertActions>
		</Alert>
	);
}

function TimesheetRow({
	row,
	isToday,
	onOpenDay,
}: {
	row: ITimesheetRow;
	isToday: boolean;
	onOpenDay: (day: string) => void;
}) {
	const status = row.entry?.status;
	const actionLabel =
		status === 'Rejected'
			? 'Fix hours'
			: status === 'Submitted'
				? 'Edit'
				: row.entry?.hours === undefined
					? 'Enter hours'
					: 'Submit';
	return (
		<DataGridRow>
			<DataGridRowItem styles={[styles.day]}>
				{isToday ? `${formatDayShort(row.day)} · Today` : formatDayShort(row.day)}
			</DataGridRowItem>
			<DataGridRowItem styles={[tt.number]}>{formatHours(row.entry?.hours)}</DataGridRowItem>
			<DataGridRowItem>
				<TimeEntryStatusPill entry={row.entry} block={row.block} isOverdue={row.isOverdue} />
			</DataGridRowItem>
			<DataGridRowItem styles={[styles.detail, status === 'Rejected' ? undefined : styles.detailMuted]}>
				{describeTimesheetRow(row, formatDateTime)}
			</DataGridRowItem>
			<DataGridRowItem styles={[tt.actionCell]}>
				{status === 'Approved' ? (
					<Button
						type='button'
						variant='ghost'
						size='sm'
						onClick={() => onOpenDay(row.day)}
						aria-label='View approved day'
					>
						<LockIcon className={css(styles.lockIcon)} />
					</Button>
				) : (
					<Button
						type='button'
						variant={status === 'Submitted' ? 'ghost' : 'outline'}
						size='sm'
						onClick={() => onOpenDay(row.day)}
					>
						{actionLabel}
					</Button>
				)}
			</DataGridRowItem>
		</DataGridRow>
	);
}

function TodayCard({
	day,
	data,
	isLoading,
	isError,
	onRetry,
	canActAsSelf,
}: {
	day: string;
	data?: ITimeEntryDay;
	isLoading: boolean;
	isError: boolean;
	onRetry: () => void;
	canActAsSelf: boolean;
}) {
	const entry = data?.entry;
	return (
		<section className={css(tt.card, tt.cardBody, styles.today)} aria-labelledby='timesheet-today-heading'>
			<div className={css(styles.todayHeader)}>
				<h2 id='timesheet-today-heading' className={css(tt.cardTitle)}>
					Today · {formatDayShort(day)}
				</h2>
				{entry ? <TimeEntryStatusPill entry={entry} /> : null}
			</div>
			{isLoading ? (
				<Skeleton styles={[styles.skeleton]} />
			) : isError ? (
				<LoadError message='We couldn’t load today’s hours.' onRetry={onRetry} />
			) : !data?.isRequired ? (
				<p className={css(tt.muted)}>No hours are needed from you today.</p>
			) : data.canEdit ? (
				<>
					<p className={css(tt.muted)}>Enter your total hours worked today.</p>
					<HoursEntryForm
						key={`${day}-${entry?.status}-${entry?.version}`}
						day={day}
						entry={entry}
						canActAsSelf={canActAsSelf}
					/>
				</>
			) : (
				<p className={css(tt.muted)}>{lockedDayMessage(data)}</p>
			)}
			{data?.warnings.map(warning => (
				<p key={warning} className={css(tt.muted)}>
					{warning}
				</p>
			))}
		</section>
	);
}

const styles = StyleSheet.create({
	action: {
		display: 'flex',
		justifyContent: 'flex-end',
	},
	columns: {
		alignItems: 'flex-start',
		display: 'flex',
		flexWrap: 'wrap',
		gap: SPACE[20],
	},
	day: {
		fontWeight: THEME.typography.fontWeights.semibold,
	},
	detail: {
		maxWidth: 360,
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		whiteSpace: 'nowrap',
	},
	detailMuted: {
		color: THEME.colors.text.main,
	},
	list: {
		display: 'flex',
		flex: '1 1 640px',
		flexDirection: 'column',
		minWidth: 0,
		overflow: 'hidden',
	},
	listHeader: {
		alignItems: 'center',
		display: 'flex',
		justifyContent: 'space-between',
		padding: `${SPACE[16]}px ${SPACE[20]}px`,
	},
	lockIcon: {
		color: THEME.colors.text.main,
		height: 16,
		width: 16,
	},
	skeleton: {
		height: 96,
		width: '100%',
	},
	today: {
		flex: '0 1 360px',
	},
	todayHeader: {
		alignItems: 'center',
		display: 'flex',
		gap: SPACE[8],
		justifyContent: 'space-between',
	},
});
